<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

Licensed under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.-->

<template>
<div class="box error" v-if="!configUI['project-image-groups-tab']">
  <h2> {{ $t('access-denied') }} </h2>
  <p>{{ $t('insufficient-permission') }}</p>
</div>
<div class="box error" v-else-if="error">
  <h2> {{ $t('error') }} </h2>
  <p>{{ $t('unexpected-error-info-message') }}</p>
</div>
<div v-else class="content-wrapper">
  <b-loading :is-full-page="false" :active="loading" />
  <div v-if="!loading" class="panel">
    <p class="panel-heading">
      {{$t('image-groups')}}
      <button v-if="canAddImage" class="button is-link" @click="addImageGroupModal = true">
        {{$t('button-add-image-group')}}
      </button>
    </p>
    <div class="panel-block">
      <div class="search-block">
        <b-input
            class="search-images"
            v-model="searchString"
            :placeholder="$t('search-placeholder')"
            type="search" icon="search"
        />
        <button class="button" @click="toggleFilterDisplay()">
        <span class="icon">
          <i class="fas fa-filter"></i>
        </span>
          <span>
          {{filtersOpened ? $t('button-hide-filters') : $t('button-show-filters')}}
        </span>
          <span v-if="nbActiveFilters" class="nb-active-filters">
          {{nbActiveFilters}}
        </span>
        </button>
      </div>

      <b-collapse :open="filtersOpened">
        <div class="filters">
          <div class="columns">
            <div class="column filter is-half">
              <div class="filter-label">
                {{$t('images')}}
              </div>
              <div class="filter-body">
                <ims-slider v-model="boundsImages" :max="maxNbImages" />
              </div>
            </div>

<!--            <div class="column filter">-->
<!--              <div class="filter-label">-->
<!--                {{$t('annotation-links')}}-->
<!--              </div>-->
<!--              <div class="filter-body">-->
<!--                <ims-slider v-model="boundsAnnotationLinks" :max="maxNbAnnotationLinks" />-->
<!--              </div>-->
<!--            </div>-->
          </div>

        </div>
      </b-collapse>

      <!-- UI-7 C6: image groups as cards; clicking a card opens the Image Group
           information pop-up (instead of an inline table expand). -->
      <div class="ig-area">
        <div v-if="pagedImageGroups.length" class="ig-grid columns is-multiline">
          <div
            class="column is-one-quarter-desktop is-one-third-tablet is-half-mobile"
            v-for="imageGroup in pagedImageGroups"
            :key="imageGroup.id"
          >
            <div class="card ig-card" :title="imageGroup.name">
              <!-- preview (the "thumbnail") opens the info pop-up; the name opens the group
                   in the viewer, mirroring the Images list. -->
              <div class="ig-preview" @click="openDetail(imageGroup)" :title="$t('information')">
                <image-group-preview
                  v-if="imageGroup.imageInstances.length > 0"
                  :image-group="imageGroup"
                  :key="`preview-${imageGroup.id}`"
                />
                <div v-else class="ig-empty"><i class="fas fa-layer-group"></i></div>
                <span class="ig-info-badge"><i class="fas fa-info-circle"></i></span>
              </div>
              <div class="card-content">
                <p class="ig-name">
                  <router-link :to="viewerURL(imageGroup)" :title="$t('button-open')">{{ imageGroup.name }}</router-link>
                </p>
                <div class="ig-meta">
                  <span class="im" :title="$t('images')"><i class="fas fa-images"></i> {{ imageGroup.numberOfImages }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="content has-text-grey has-text-centered no-ig">
          <p>{{$t('no-image-group')}}</p>
        </div>

        <pager :total="filteredImageGroups.length" :per-page="Number(perPage)" :current.sync="currentPage" />
      </div>

      <image-group-detail-modal
        :active.sync="detailModalActive"
        :image-group="detailImageGroup"
        @delete="refreshData"
        @deleteImage="refreshData"
        @addToImageGroup="refreshData"
      />
    </div>

    <add-image-group-modal :active.sync="addImageGroupModal" @newImageGroup="newImageGroup" />
  </div>
</div>
</template>

<script>
import {get, sync, syncBoundsFilter} from '@/utils/store-helpers';

import ImsSlider from '@/components/form/ImsSlider';

import ImageGroupPreview from '@/components/image-group/ImageGroupPreview';
import ImageGroupDetailModal from '@/components/image-group/ImageGroupDetailModal';
import AddImageGroupModal from '@/components/image-group/AddImageGroupModal';
import AddToImageGroupModal from '@/components/image-group/AddToImageGroupModal';
import Pager from '@/components/utils/Pager';

import {ImageGroupCollection} from '@/api';
import {getWildcardRegexp} from '@/utils/string-utils';
import {isBetweenBounds} from '@/utils/bounds';


// store options to use with store helpers to target projects/currentProject/listImages module
const storeOptions = {rootModuleProp: 'storeModule'};
// redefine helpers to use storeOptions and correct module path
const localSyncBoundsFilter = (filterName, maxProp) => syncBoundsFilter(null, filterName, maxProp, storeOptions);


export default {
  name: 'list-image-groups',
  components: {
    ImageGroupPreview,
    ImageGroupDetailModal,
    Pager,
    ImsSlider,
    AddImageGroupModal
  },
  data() {
    return {
      loading: true,
      error: false,
      imageGroups: [],
      addImageGroupModal: false,
      detailModalActive: false,
      detailImageGroup: null
    };
  },
  computed: {
    currentUser: get('currentUser/user'),
    configUI: get('currentProject/configUI'),
    project: get('currentProject/project'),
    blindMode() {
      return this.project.blindMode;
    },
    canManageProject() {
      return this.$store.getters['currentProject/canManageProject'];
    },
    canAddImage() {
      return !this.currentUser.guestByNow && (this.canManageProject || !this.project.isReadOnly);
    },

    storeModule() { // path to the vuex module in which state of this component is stored (projects/currentProject/listImages)
      return this.$store.getters['currentProject/currentProjectModule'] + 'listImageGroups';
    },

    searchString: sync('searchString', {...storeOptions, debounce: 500}),
    filtersOpened: sync('filtersOpened', storeOptions),

    maxNbImages() {
      return Math.max(10, ...this.imageGroups.map(ig => ig.numberOfImages));
    },
    maxNbAnnotationLinks() {
      return 100; //Math.max(100, ...this.imageGroups.map(ig => ig.numberOfAnnotationLinks));
    },

    boundsImages: localSyncBoundsFilter('boundsImages', 'maxNbImages'),
    boundsAnnotationLinks: localSyncBoundsFilter('boundsAnnotationLinks', 'maxNbAnnotationLinks'),

    regexp() {
      return getWildcardRegexp(this.searchString);
    },
    filteredImageGroups() {
      let filtered = this.imageGroups;

      if (this.searchString) {
        filtered = filtered.filter(group => this.regexp.test(group.name));
      }

      filtered = filtered.filter(group => {
        return isBetweenBounds(group.numberOfImages, this.boundsImages) /*&&
          isBetweenBounds(group.numberOfAnnotationLinks, this.boundsAnnotationLinks)*/;
      });

      return filtered;
    },

    // UI-7 C6: client-side page slice for the card grid (data is fully in memory).
    pagedImageGroups() {
      let per = Number(this.perPage) || 10;
      let start = (this.currentPage - 1) * per;
      return this.filteredImageGroups.slice(start, start + per);
    },


    nbActiveFilters() {
      return this.$store.getters[this.storeModule + '/nbActiveFilters'];
    },
    nbEmptyFilters() {
      return this.$store.getters[this.storeModule + '/nbEmptyFilters'];
    },

    currentPage: sync('currentPage', storeOptions),
    perPage: sync('perPage', storeOptions),
    sort: sync('sort', storeOptions),
    openedDetailsStore: get('openedDetails', storeOptions),
    openedDetails: { // HACK cannot use sync because buefy modifies the property => vuex warning because modif outside store
      get() {
        return this.openedDetailsStore.slice();
      },
      set(value) {
        this.$store.commit(this.storeModule + '/setOpenedDetails', value);
      }
    }
  },
  methods: {
    async fetchImageGroups() {
      this.imageGroups = (await ImageGroupCollection.fetchAll({
        filterKey: 'project',
        filterValue: this.project.id,
      })).array;
    },

    async refreshData() {
      try {
        await this.fetchImageGroups();

        // if an item has been deleted, the currentPage value might not be valid => reinitialize it
        if ((this.currentPage - 1) * this.perPage >= this.filteredImageGroups.length) {
          this.currentPage = 1;
        }
      } catch (error) {
        console.log(error);
        this.error = true;
      }
    },

    toggleFilterDisplay() {
      this.filtersOpened = !this.filtersOpened;
    },

    openDetail(imageGroup) {
      this.detailImageGroup = imageGroup;
      this.detailModalActive = true;
    },

    updateSort(field, order) {
      this.sort = {field, order};
    },

    viewerURL(imageGroup) {
      let ids = imageGroup.imageInstances.map(img => img.id);
      return `/project/${imageGroup.project}/image/${ids.join('-')}`;
    },

    newImageGroup(imageGroup) {
      this.refreshData();
      this.addImageGroupModal = false;
      this.$buefy.modal.open({
        parent: this,
        component: AddToImageGroupModal,
        hasModalCard: true,
        props: {imageGroup, programmatic: true, active: true},
        events: {
          'addToImageGroup': this.refreshData,
        }
      });

    }
  },
  async created() {
    try {
      await Promise.all([
        this.fetchImageGroups()
      ]);
      this.loading = false;
    } catch (error) {
      console.log(error);
      this.error = true;
    }
  }
};
</script>

<style scoped>
/* UI-4b: full-bleed white ZayaAI surface */
.content-wrapper { padding: 0; }

.panel-heading {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-block {
  display: flex;
}

>>> .search-images {
  max-width: 30rem;
  margin-right: 1rem;
}

>>> td, >>> th {
  vertical-align: middle !important;
}

/* UI-7 C6: image-group card grid */
.ig-area {
  position: relative;
  margin-top: 1rem;
  min-height: 10rem;
}
.no-ig {
  padding: 3rem 0;
}
.ig-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid #e5e8ee;
  box-shadow: none;
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
}
.ig-card:hover {
  border-color: #1e63d0;
  box-shadow: 0 6px 18px rgba(15, 17, 22, 0.08);
}
.ig-preview {
  position: relative;
  height: 120px;
  overflow: hidden;
  border-bottom: 1px solid #e5e8ee;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f4f5f7;
  cursor: pointer;
}
.ig-info-badge {
  position: absolute;
  bottom: 6px;
  right: 8px;
  color: #1e63d0;
  opacity: 0.55;
  font-size: 0.85rem;
}
.ig-preview:hover .ig-info-badge { opacity: 1; }
.ig-empty {
  color: #b5bcc9;
  font-size: 2rem;
}
.card-content {
  padding: 0.7rem 0.85rem;
  flex: 1;
}
.ig-name {
  font-weight: 700;
  margin-bottom: 0.45rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.ig-name a {
  color: #1e63d0;
}
.ig-name a:hover {
  color: #17509e;
  text-decoration: underline;
}
.ig-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.8rem;
  color: #5a6172;
}
.ig-meta .im i { margin-right: 0.25rem; }
</style>
